import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import InlineError from 'components/UI/InlineError';
import useLiterals from 'utils/hooks/useLiterals';
import { groupNotificationsByDate } from 'modules/notifications/mapNotification';
import NotificationCard from './NotificationCard';

const NotificationList = ({
  items,
  loading,
  loadingMore,
  last,
  error,
  hasFilters,
  onOpen,
  onLoadMore,
}) => {
  const literals = useLiterals('notifications');
  const sentinelRef = useRef(null);
  const groups = groupNotificationsByDate(items);
  const canObserve = typeof IntersectionObserver === 'function';
  const showFallback = (!canObserve || Boolean(error))
    && !last
    && !loading
    && items.length > 0;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!canObserve || !node || error || last) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        onLoadMore();
      }
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [canObserve, error, items.length, last, onLoadMore]);

  if (loading && items.length === 0) {
    return <p className='text-neutral-500'>{literals.loading}</p>;
  }

  if (error && items.length === 0) {
    return <InlineError error={literals[error] || literals.loadError} />;
  }

  if (!items.length) {
    return (
      <p className='m-2 text-center'>
        {hasFilters ? literals.noResults : literals.noNotifications}
      </p>
    );
  }

  return (
    <div>
      <h2 className='mb-4 text-base'>{literals.sectionTitle}</h2>
      {groups.map((group) => (
        <section key={group.key || group.label} className='mb-4'>
          {group.label ? (
            <h3 className='mb-2 text-sm text-neutral-500'>{group.label}</h3>
          ) : null}
          {group.items.map((item, index) => (
            <NotificationCard
              key={item.id || `${group.key}-${index}`}
              item={item}
              onOpen={onOpen}
            />
          ))}
        </section>
      ))}
      {error ? (
        <InlineError className='mb-4' error={literals[error] || literals.loadError} />
      ) : null}
      {loadingMore ? (
        <p className='text-neutral-500 text-center'>{literals.loading}</p>
      ) : null}
      {showFallback ? (
        <Button
          type='outline-primary'
          text={literals.loadMore}
          onClick={onLoadMore}
          disabled={loadingMore}
          className='mt-2'
        />
      ) : null}
      {canObserve ? <div ref={sentinelRef} className='h-1' /> : null}
    </div>
  );
};

NotificationList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  loadingMore: PropTypes.bool.isRequired,
  last: PropTypes.bool.isRequired,
  error: PropTypes.string,
  hasFilters: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
};

export default NotificationList;
